import React, { useState } from 'react';
import { api } from '../services/api';
import { useAppStore } from '../store/useAppStore';

interface RegisterSellerFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

const RegisterSellerForm: React.FC<RegisterSellerFormProps> = ({ onSuccess, onCancel }) => {
  const { setSeller } = useAppStore();
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const [password, setPassword] = useState('');
  const [isLoginMode, setIsLoginMode] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateAndNormalizePhone = (input: string): string | null => {
    const cleaned = input.replace(/\D/g, '');
    if (cleaned.length === 7) return `597${cleaned}`;
    if (cleaned.length === 8 && cleaned.startsWith('0')) return `597${cleaned.substring(1)}`;
    if (cleaned.length === 10 && cleaned.startsWith('597')) return cleaned;
    if (cleaned.length === 13 && cleaned.startsWith('00597')) return cleaned.substring(2);
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    const normalizedContact = validateAndNormalizePhone(contact);
    if (!normalizedContact) {
        setError('Please enter a valid Suriname mobile number.');
        setIsSubmitting(false);
        return;
    }

    try {
        let user;
        if (isLoginMode) {
            user = await api.auth.login(normalizedContact, password);
        } else {
            if (!name.trim()) {
                setError('Please enter a name for your new account.');
                setIsSubmitting(false);
                return;
            }
            user = await api.auth.register(name, normalizedContact, password);
        }
        setSeller(user);
        onSuccess();
    } catch (err: any) {
        if (!isLoginMode && err.message?.includes('exists')) {
            setIsLoginMode(true);
            setError('Account found! Please click "Log In" below.');
        }
        else if (isLoginMode && err.message?.includes('not found')) {
            setIsLoginMode(false);
            setError('Account not found. Please enter your name to register.');
        } else {
            setError(err.message || 'An error occurred.');
        }
    } finally {
        setIsSubmitting(false);
    }
  };

  const inputClass = "w-full px-3 py-2 bg-input-bg text-input-text border border-input-border rounded-md hover:border-input-border-hover focus:outline-none focus:border-brand-green focus:ring-2 focus:ring-focus-ring";
  const labelClass = "block text-sm font-medium text-app-text mb-1";

  return (
    <div className="bg-card-bg p-6 rounded-lg shadow-lg border border-card-border max-w-md mx-auto mt-10">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-card-title">{isLoginMode ? 'Welcome Back' : 'Create Seller Profile'}</h2>
        <p className="text-app-text-secondary mt-2">
            {isLoginMode
                ? 'Enter your number and password to access your dashboard.'
                : 'Set up your profile to start listing products.'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
            <div role="alert" className="bg-error-bg text-error-text p-3 rounded-md text-sm border border-error-border">
                {error}
            </div>
        )}

        {!isLoginMode && (
            <div>
              <label htmlFor="reg-name" className={labelClass}>Your Name / Farm Name*</label>
              <input
                type="text"
                id="reg-name"
                value={name}
                onChange={e => setName(e.target.value)}
                className={inputClass}
                required={!isLoginMode}
                placeholder="e.g. Green Valley Farms"
              />
            </div>
        )}

        <div>
          <label htmlFor="reg-contact" className={labelClass}>WhatsApp Number (+597)*</label>
          <div className="relative">
              <input
                type="tel"
                id="reg-contact"
                value={contact}
                onChange={e => setContact(e.target.value)}
                className={inputClass}
                required
                placeholder="e.g. 8801234"
              />
          </div>
        </div>

        <div>
          <label htmlFor="reg-password" className={labelClass}>Password (at least 10 characters)*</label>
          <input type="password" id="reg-password" value={password}
            onChange={e => setPassword(e.target.value)} className={inputClass}
            required minLength={10} maxLength={128}
            autoComplete={isLoginMode ? 'current-password' : 'new-password'} />
        </div>

        <div className="flex justify-between items-center pt-2">
             <button
                type="button"
                onClick={() => {
                    setIsLoginMode(!isLoginMode);
                    setError('');
                }}
                className="text-sm font-medium text-brand-green underline underline-offset-2 hover:text-brand-green-hover"
             >
                {isLoginMode ? "Need an account?" : "Already have an account?"}
             </button>
        </div>

        <div className="flex justify-end gap-4 pt-4 border-t border-divider mt-2">
            <button type="button" onClick={onCancel} className="py-2 px-4 bg-btn-secondary-bg text-btn-secondary-text border border-btn-secondary-border rounded-md hover:bg-btn-secondary-hover font-medium transition-colors">Cancel</button>
            <button
                type="submit"
                disabled={isSubmitting}
                className="py-2 px-6 bg-btn-primary text-btn-primary-text font-bold rounded-md hover:bg-btn-primary-hover disabled:opacity-70 disabled:cursor-not-allowed transition-colors"
            >
                {isSubmitting ? 'Processing...' : (isLoginMode ? 'Log In' : 'Register')}
            </button>
        </div>
      </form>
    </div>
  );
};

export default RegisterSellerForm;
